import { useEffect, useState } from 'react'
import { Loader2, Sparkles, X } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Checkbox } from 'components/ui/checkbox'
import { Input } from 'components/ui/input'
import { Select } from 'components/ui/select'
import { Sheet } from 'components/ui/sheet'
import { Textarea } from 'components/ui/textarea'
import { suggestRisks, type RiskSuggestion, type RiskSuggestions } from 'services/aiService'

/** One risk to create: what the user left ticked, with their edits. */
export interface RiskToApply {
	name: string
	category: string
	likelihood: number
	impact: number
	mitigation: string
	description: string
}

interface Row {
	suggestion: RiskSuggestion
	checked: boolean
	name: string
	category: string
	likelihood: number
	impact: number
	mitigation: string
}

const LEVELS = [1, 2, 3, 4, 5]

/** "Based on 9 facts: 5 site details · 1 demographic area · …" */
export const basedOnText = (basedOn: RiskSuggestions['basedOn']): string =>
	`${basedOn.facts} fact${basedOn.facts === 1 ? '' : 's'}: ` +
	[
		`${basedOn.siteFacts} site detail${basedOn.siteFacts === 1 ? '' : 's'}`,
		basedOn.demographics ? `${basedOn.demographics} demographic area${basedOn.demographics === 1 ? '' : 's'}` : 'no demographics',
		basedOn.retailers ? `${basedOn.retailers} saved nearby retailer${basedOn.retailers === 1 ? '' : 's'}` : 'no saved nearby retailers',
		basedOn.prosAndCons ? `${basedOn.prosAndCons} pros and cons` : null,
		basedOn.financials === 'included' ? 'financials' : basedOn.financials === 'not on this plan' ? 'financials not included on your plan' : null,
	]
		.filter(Boolean)
		.join(' · ')

/**
 * The description saved with an accepted suggestion: says it came from Ask MDT
 * and what it rested on, so anyone reading the register later knows where the
 * risk came from.
 */
export const descriptionFor = (suggestion: RiskSuggestion): string => {
	const evidence = suggestion.evidence.map(fact => fact.label).join('; ')
	return `Suggested by Ask MDT: ${suggestion.rationale}${evidence ? ` Evidence: ${evidence}.` : ''}`
}

const messageOf = (error: any): string => error?.response?.data?.message || "Ask MDT couldn't suggest risks just now. Try again in a moment."

/**
 * "Suggest risks" for one site (MYD-647). The user picks a site and asks; the
 * call is not made on open, because each one is a model call on the user's
 * budget. Each suggestion shows its rationale and the facts it cites, with
 * the name, category, levels and mitigation editable.
 *
 * Nothing is written until Apply, and then only the ticked rows, through
 * `onApply`: the ordinary create-risk endpoint, as the user. Where the user
 * cannot create risks (`canApply` false: read-only or impersonating) the sheet
 * still shows the suggestions, and says why Apply is off.
 */
export function RiskSuggestionsSheet({
	open,
	onClose,
	evaluationId,
	sites,
	canApply,
	onApply,
	suggest = suggestRisks,
}: {
	open: boolean
	onClose: () => void
	evaluationId: string
	sites: { id: string; name: string }[]
	canApply: boolean
	onApply: (siteId: string, risks: RiskToApply[]) => Promise<void>
	suggest?: typeof suggestRisks
}) {
	const [siteId, setSiteId] = useState(sites[0]?.id ?? '')
	const [result, setResult] = useState<RiskSuggestions | null>(null)
	const [rows, setRows] = useState<Row[]>([])
	const [loading, setLoading] = useState(false)
	const [applying, setApplying] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// A site removed while the sheet was shut is not left selected.
	useEffect(() => {
		if (!sites.some(site => site.id === siteId)) setSiteId(sites[0]?.id ?? '')
	}, [sites, siteId])

	const ask = async () => {
		setLoading(true)
		setError(null)
		setResult(null)
		try {
			const answer = await suggest(evaluationId, siteId)
			setResult(answer)
			setRows(answer.suggestions.map(suggestion => ({ suggestion, checked: true, name: suggestion.name, category: suggestion.category, likelihood: suggestion.likelihood, impact: suggestion.impact, mitigation: suggestion.mitigation })))
		} catch (failure) {
			setError(messageOf(failure))
		} finally {
			setLoading(false)
		}
	}

	const update = (index: number, patch: Partial<Row>) => setRows(current => current.map((row, i) => (i === index ? { ...row, ...patch } : row)))

	const chosen = rows.filter(row => row.checked)
	const unnamed = chosen.some(row => !row.name.trim())

	const apply = async () => {
		if (!result) return
		setApplying(true)
		setError(null)
		try {
			await onApply(
				result.siteId,
				chosen.map(row => ({ name: row.name.trim(), category: row.category, likelihood: row.likelihood, impact: row.impact, mitigation: row.mitigation.trim(), description: descriptionFor(row.suggestion) })),
			)
			onClose()
		} catch (failure: any) {
			setError(typeof failure === 'string' ? failure : failure?.message || 'Some risks could not be added. Check the register and try again.')
		} finally {
			setApplying(false)
		}
	}

	return (
		<Sheet open={open} onClose={onClose} side="right" className="w-full sm:w-[560px]">
			<div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
				<div className="min-w-0">
					<h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
						<Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
						Suggested risks
					</h2>
					<p className="text-sm text-gray-600">Up to eight risks this site's register doesn't hold yet, from what is recorded about it.</p>
				</div>
				<button type="button" onClick={onClose} aria-label="Close suggested risks" className="rounded p-1 text-gray-500 hover:bg-gray-100">
					<X className="h-4 w-4" />
				</button>
			</div>

			<div className="flex-1 space-y-3 px-5 py-4">
				<div className="flex flex-wrap items-center gap-2">
					<Select value={siteId} onChange={event => setSiteId(event.target.value)} aria-label="Site" className="h-9 w-auto min-w-0 flex-1 text-sm" disabled={loading}>
						{sites.map(site => (
							<option key={site.id} value={site.id}>
								{site.name}
							</option>
						))}
					</Select>
					<Button type="button" variant="primary" onClick={ask} disabled={loading || !siteId}>
						{loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />}
						{loading ? 'Suggesting…' : result ? 'Suggest again' : 'Suggest risks'}
					</Button>
				</div>

				{loading && (
					<p className="text-sm text-gray-600" role="status">
						Reading this site's details, demographics, nearby retailers and pros and cons…
					</p>
				)}
				{error && (
					<p role="alert" className="text-sm text-destructive">
						{error}
					</p>
				)}
				{result && (
					<>
						<p className="text-xs text-gray-500">
							AI suggestions: nothing is added until you apply. Based on {basedOnText(result.basedOn)}. MDT holds no flood, zoning or traffic data, so check what is not covered here.
						</p>
						<ul className="space-y-2">
							{rows.map((row, index) => {
								const { suggestion } = row
								return (
									<li key={suggestion.name} className="rounded-lg border border-gray-200 p-3">
										<div className="flex items-start gap-3">
											<Checkbox checked={row.checked} onCheckedChange={checked => update(index, { checked })} aria-label={`Add ${suggestion.name}`} className="mt-2" />
											<div className="min-w-0 flex-1 space-y-2">
												<Input value={row.name} onChange={event => update(index, { name: event.target.value, checked: true })} aria-label={`Name for ${suggestion.name}`} className="h-8 text-sm font-medium" />
												<div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
													<Select value={row.category} onChange={event => update(index, { category: event.target.value })} aria-label={`Category for ${suggestion.name}`} className="h-8 w-auto text-xs">
														{result.categories.map(category => (
															<option key={category} value={category}>
																{category}
															</option>
														))}
													</Select>
													<label className="flex items-center gap-1">
														Likelihood
														<Select value={row.likelihood} onChange={event => update(index, { likelihood: Number(event.target.value) })} aria-label={`Likelihood for ${suggestion.name}`} className="h-8 w-14 text-xs">
															{LEVELS.map(level => (
																<option key={level} value={level}>
																	{level}
																</option>
															))}
														</Select>
													</label>
													<label className="flex items-center gap-1">
														Impact
														<Select value={row.impact} onChange={event => update(index, { impact: Number(event.target.value) })} aria-label={`Impact for ${suggestion.name}`} className="h-8 w-14 text-xs">
															{LEVELS.map(level => (
																<option key={level} value={level}>
																	{level}
																</option>
															))}
														</Select>
													</label>
												</div>
												<p className="text-sm text-gray-700">{suggestion.rationale}</p>
												<Textarea value={row.mitigation} onChange={event => update(index, { mitigation: event.target.value })} aria-label={`Mitigation for ${suggestion.name}`} placeholder="Mitigation" rows={2} className="text-sm" />
												<ul className="space-y-0.5 border-l-2 border-gray-200 pl-2">
													{suggestion.evidence.map(fact => (
														<li key={fact.id} className="text-xs text-gray-500">
															<span className="font-medium text-gray-600">{fact.label}:</span> {fact.text}
														</li>
													))}
												</ul>
											</div>
										</div>
									</li>
								)
							})}
						</ul>
					</>
				)}
			</div>

			{result && (
				<div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-3">
					<p className="text-xs text-gray-500">{!canApply ? 'Your account cannot add risks here.' : unnamed ? 'Every ticked risk needs a name.' : `${chosen.length} of ${rows.length} ticked`}</p>
					<Button type="button" variant="primary" onClick={apply} disabled={!canApply || applying || chosen.length === 0 || unnamed}>
						{applying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
						{applying ? 'Adding…' : `Add ${chosen.length} risk${chosen.length === 1 ? '' : 's'}`}
					</Button>
				</div>
			)}
		</Sheet>
	)
}

export default RiskSuggestionsSheet
